import { Button, ButtonProps, styled } from "@mui/material";
import { getAllDataInQueryString } from "helpers/search";
import useAuth from "hooks/useAuth";
import useNavigate from "hooks/useNavigate";
import { useLoading } from "providers/loading";
import { useLanguageContext } from "providers/mul-language";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import { IResolveParams, LoginSocialGoogle } from "reactjs-social-login";
import { Img } from "Components/Img";
import { GOOGLE_ICON } from "Assets/icons";
import { TextSemibold } from "Components/Text";
import { GG_APP_ID } from "constants/schemas";
import { apiRegisterWithGg } from "services/api/auth";
import { PATHS, SEARCH_PARAMS } from "constants/routes";
import { encodeBase64 } from "helpers/base64";
import { concatUrl } from "helpers/url";

const GoogleButton = (
  props: ButtonProps & {
    hideText?: boolean;
    onCallback?: any;
    text?: string;
    onCustomConfirm?: (data: any) => void;
    onClose?: () => void;
  }
) => {
  const {
    text = "login_gg",
    onCustomConfirm,
    onClose,
    hideText,
    ...rest
  } = props;
  const { useSearchParams } = useNavigate();
  const redirect_uri = `${window.location.protocol}//${window.location.host}`;
  const { setLoading } = useLoading();
  const { language } = useLanguageContext();
  const intl = useIntl();
  const { onGetUserInformation, onDirectNextPageOfLogin } = useAuth();

  const onConfirm = useCallback((data: any) => {
    if (onCustomConfirm) {
      onCustomConfirm(data);
    } else {
      const { code } = data || {};
      setLoading(true);
      const queryData = getAllDataInQueryString(useSearchParams);

      apiRegisterWithGg({
        code,
        language,
        redirect_uri,
        ...queryData,
        check_signup: true,
      })
        .then((res) => {
          setLoading(false);
          if (props.onCallback) {
            props.onCallback(res);
          } else {
            if (res?.data?.is_signed_up) {
              onGetUserInformation()
                .then((err) => {
                  onDirectNextPageOfLogin();
                  onClose?.();
                })
                .catch((err) => {
                  console.log(false);
                });
            } else {
              const searchParams = new URLSearchParams({
                [SEARCH_PARAMS.uid]: code,
                [SEARCH_PARAMS.mail]: res.data.user,
                [SEARCH_PARAMS.username]: res.data.full_name,
                [SEARCH_PARAMS.directUrl]: encodeBase64(
          window.location.pathname + window.location.search + window.location.hash
        ),
              }).toString();
              window.location.href = concatUrl(
                PATHS.registerFull,
                searchParams
              );
            }
          }
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoginSocialGoogle
      client_id={GG_APP_ID || ""}
      redirect_uri={redirect_uri}
      scope="openid profile email"
      discoveryDocs="claims_supported"
      access_type="offline"
      onResolve={({ data }: IResolveParams) => {
        onConfirm(data);
      }}
      onReject={(err) => {
        console.log(err);
      }}
    >
      <StyledButton fullWidth {...rest}>
        <Img src={GOOGLE_ICON} alt="icon" />
        {!hideText && (
          <TextSemibold>{intl.formatMessage({ id: text })}</TextSemibold>
        )}
      </StyledButton>
    </LoginSocialGoogle>
  );
};

export const StyledButton = styled(Button)({
  padding: "10px 16px",
  border: "1px solid rgba(0,0,0,.26)",
  textTransform: "initial",
  color: "#000",
  justifyContent: "center",
  display: "flex",
  gap: 12,
  alignItems: "center",
  "& img": {
    width: 24,
  },
});

export default GoogleButton;
